import {
	HTMLAttributes,
	ReactNode,
	useEffect,
	useRef,
	useCallback,
	useMemo,
} from "react";
import { BaseProps } from "@animech/ui";
import interact from "interactjs";
import { FieldProps } from "@animech/axiom-ui-builder";
import * as Styled from "./custom-product-list.style";
import { ProductId, Root } from "@definitions";
import { setElementDragAndDropPayload } from "src/core/drag-and-drop/element-drag-and-drop-payload";
import { useUiData } from "src/ui/data";

type ExtendedFieldProps = FieldProps<Root> & {
	draggable?: boolean;
};

export type CustomProductListProps = BaseProps<HTMLAttributes<HTMLDivElement>> &
	FieldProps<Root> & {
		draggable?: boolean;
		onProductSelect?(product: unknown): void;
		onProductDrag?(product: unknown, dragEvent: Interact.DragEvent): void;
		renderField?: (props: ExtendedFieldProps) => ReactNode;
	};

export const CustomProductList = (props: CustomProductListProps) => {
	const {
		children,
		renderField,
		rootRef,
		rootAttributes,
		id,
		className,
		draggable,
		onProductSelect,
		onProductDrag,
		...fieldPropsFromParent
	} = props;
	const { getInStockDomainFromProductId } = useUiData();

	const outOfStockIdexes = useMemo(
		() =>
			fieldPropsFromParent.fieldValues?.reduce((acc: number[], fv, idx) => {
				const isInStock =
					getInStockDomainFromProductId(fv as ProductId).length > 0;

				if (!isInStock) {
					acc.push(idx);
				}
				return acc;
			}, [] as number[]),
		[fieldPropsFromParent.fieldValues, getInStockDomainFromProductId],
	);

	const currentValue = fieldPropsFromParent.value;

	const inStockFieldValues = useMemo(
		() =>
			fieldPropsFromParent.fieldValues?.filter((fv, idx) => {
				if (!outOfStockIdexes) {
					return true;
				}
				// Keep OOS item if it's the currently selected value
				if (outOfStockIdexes.includes(idx) && fv === currentValue) {
					return true;
				}
				return !outOfStockIdexes.includes(idx);
			}),
		[fieldPropsFromParent.fieldValues, outOfStockIdexes, currentValue],
	);

	const inStockFieldMeta = useMemo(
		() =>
			fieldPropsFromParent.fieldValuesMeta?.filter((_, idx) => {
				if (!outOfStockIdexes) {
					return true;
				}
				// Keep OOS item meta if it's the currently selected value
				const fv = fieldPropsFromParent.fieldValues?.[idx];
				if (outOfStockIdexes.includes(idx) && fv === currentValue) {
					return true;
				}
				return !outOfStockIdexes.includes(idx);
			}),
		[
			fieldPropsFromParent.fieldValuesMeta,
			fieldPropsFromParent.fieldValues,
			outOfStockIdexes,
			currentValue,
		],
	);

	const fieldProps: FieldProps<Root> = {
		...fieldPropsFromParent,
		fieldValues: inStockFieldValues,
		fieldValuesMeta: inStockFieldMeta,
		onValueChange: (value, i) => {
			if (fieldPropsFromParent.onValueChange) {
				fieldPropsFromParent.onValueChange(value, i);
			}
		},
	};

	const componentRef = useRef<HTMLDivElement>(null);

	const getProductId = useCallback(
		(element: HTMLElement) => {
			const input = element.getElementsByTagName("input")[0];
			const valueIndex = parseInt(input.value, 10);
			const value = fieldProps.fieldValues?.[valueIndex];
			return value;
		},
		[fieldProps.fieldValues],
	);

	const onDrag = useCallback(
		(e: Interact.DragEvent) => {
			if (e.button > 0) {
				return;
			}
			const element = e.target as HTMLElement;
			const value = getProductId(element);

			if (e.type === "dragstart") {
				// Copy different elements based on if there is a thumbnail or not
				let target: HTMLElement = element.getElementsByTagName("div")[0];
				if (
					!target ||
					!target.style.backgroundImage ||
					target.style.backgroundImage === ""
				) {
					target = e.target as HTMLElement;
				}

				const container = target.cloneNode(true) as HTMLElement;
				container.id = "dragging-element";
				document.body.appendChild(container);
			} else if (e.type === "dragmove") {
				const draggableElement = document.getElementById("dragging-element");
				if (draggableElement) {
					draggableElement.style.opacity = e.dropzone ? "0" : "0.75";
					draggableElement.style.transform = `translate(calc(${e.clientX}px - 50%), calc(${e.clientY}px - 50%))`;
				}
			} else if (e.type === "dragend") {
				const draggableElement = document.getElementById("dragging-element");
				if (draggableElement) {
					document.body.removeChild(draggableElement);
				}
			}

			if (value) {
				onProductDrag?.(value, e);
			}
		},
		[getProductId, onProductDrag],
	);

	useEffect(() => {
		const rootElement = componentRef.current;
		if (!rootElement) {
			return;
		}

		const productNodes = rootElement.querySelectorAll("div[draggable=true]");

		for (const element of Array.from(productNodes)) {
			const isHTMLElement = element instanceof HTMLElement;
			if (!isHTMLElement) {
				continue;
			}

			setElementDragAndDropPayload(element, {
				productId: getProductId(element) as ProductId,
			});
			interact(element)
				.draggable({
					listeners: {
						start: onDrag,
						move: onDrag,
						end: onDrag,
					},
				})
				.preventDefault("always")
				.styleCursor(false);
		}
	}, [onDrag, getProductId]);

	return (
		<Styled.CustomProductList ref={componentRef}>
			{renderField
				? renderField({
						...fieldProps,
						value: null,
						draggable,
						onValueChange: onProductSelect
							? (value) => !onProductDrag && onProductSelect(value)
							: undefined,
					})
				: null}
		</Styled.CustomProductList>
	);
};
